/** @jsx jsx */
import React, { type PropsWithChildren } from 'react';
import { jsx } from '@compiled/react';
import { SERVICE_DESK_PROJECT } from '@atlassian/jira-common-constants/src/project-types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import ErrorBoundary from '@atlassian/jira-error-boundary/src/ErrorBoundary.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import Content from '../content/index.tsx';
import { IssueCommentEditorReadonly } from '../content/async.tsx';
import type { OwnProps as CommentContentProps } from '../content/view.tsx';

import CommentVisibilitySelector from '../comment-visibility/index.tsx';
import { EventOccurredAtSelector } from '../event-occurred-at-selector/index.tsx';
import { useCommentContext } from './CommentContextProvider.tsx';
import { useEditSave, useHideSave } from './utils.tsx';
import AkCommentContent from './AkCommentContent.tsx';

const CommentContentAIWrapper = ({ children }: PropsWithChildren) => {
	const { isDeleted, isEditing, commentRef } = useCommentContext();
	if (isDeleted) {
		return null;
	}
	if (!isEditing && fg('thor_ai_definitions_issue_comment')) {
		return <div ref={commentRef}>{children}</div>;
	}
	return children;
};

const CommentContentDefault: React.FC = () => {
	const {
		bodyAdf,
		bodyHtml,
		commentId,
		isEditing,
		onEditCancel,
		onEditUpdate,
		onEditPaste,
		onBlur,
		visibilityEdited,
		onCommentVisibilityChange,
		shouldShowCommentVisibility,
		eventOccurredAt,
		isEditingInternal,
		projectType,
		parentId,
		canEdit,
		mentionProvider,
		contextIdentifier,
		mediaContext,
		setSelectedEventOccuredAt,
		pinnedCommentBodyAdf,
		isPinned,
	} = useCommentContext();

	const onEditSave = useEditSave();
	const shouldShowCommentVisibilitySelector = shouldShowCommentVisibility && !parentId; // should not show for replies
	const onHideSave = useHideSave();
	const jsmTimelineComponents = isEditingInternal
		? [
				<EventOccurredAtSelector
					key="eventOccurredAtSelector"
					initialValue={eventOccurredAt}
					onChange={(newEventOccurredAt: number | null) => {
						setSelectedEventOccuredAt?.(newEventOccurredAt);
					}}
				/>,
			]
		: [];
	let secondaryToolbarComponents = shouldShowCommentVisibilitySelector
		? [
				...jsmTimelineComponents,
				<CommentVisibilitySelector
					key="commentVisibilitySelector"
					initialValue={visibilityEdited}
					onChange={onCommentVisibilityChange}
				/>,
			]
		: jsmTimelineComponents;
	secondaryToolbarComponents =
		shouldShowCommentVisibilitySelector &&
		(projectType === SERVICE_DESK_PROJECT ? isEditingInternal : true)
			? [
					...jsmTimelineComponents,
					<CommentVisibilitySelector
						key="commentVisibilitySelector"
						initialValue={visibilityEdited}
						onChange={onCommentVisibilityChange}
					/>,
				]
			: jsmTimelineComponents;
	let commentContentProps: CommentContentProps = {
		commentId,
		isEditing,
		secondaryToolbarComponents,
		canEdit,
		onCancel: onEditCancel,
		onChange: onEditUpdate,
		onSave: onEditSave,
		onPaste: onEditPaste,
		onBlur,
		onHideSave,
	};

	if (fg('change_comment_editorstate_props_to_propdrill')) {
		commentContentProps = {
			...commentContentProps,
			mentionProvider,
			contextIdentifier,
			mediaContext,
		};
	}
	if (__SERVER__) {
		return (
			<CommentContentAIWrapper>
				<Content {...commentContentProps} bodyAdf={isPinned ? pinnedCommentBodyAdf : bodyAdf} />
			</CommentContentAIWrapper>
		);
	}
	return (
		<CommentContentAIWrapper>
			<ErrorBoundary
				id="issue-comment-editor"
				packageName="issue"
				render={() => <IssueCommentEditorReadonly bodyHtml={bodyHtml} />}
			>
				<Content {...commentContentProps} bodyAdf={isPinned ? pinnedCommentBodyAdf : bodyAdf} />
			</ErrorBoundary>
		</CommentContentAIWrapper>
	);
};

const CommentContent = componentWithFG(
	'jira_custom_comment_component_refactor',
	CommentContentDefault,
	AkCommentContent,
);

export default CommentContent;
